import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { compose } from 'redux';

import Context from 'redpoints-connector-common/model/Context';

import { Col, Notification, Row } from 'redpoints-front-components/index';
import { withContext } from 'redpoints-front-common/components';
import { isValidUrl } from 'redpoints-front-common/helpers';

import {
  shortenerResolutionCloseLink,
  shortenerResolutionDelayLink,
  shortenerResolutionFetchLinkToResolve,
  shortenerResolutionResolveLink,
} from '../../../actions/shortenerResolutionActions';
import {
  getShortenerResolutionActionsAreLoading,
  getShortenerResolutionCloseLinkIsSuccess,
  getShortenerResolutionDelayLinkIsSuccess,
  getShortenerResolutionFiltersSelected,
  getShortenerResolutionLinkToResolve,
  getShortenerResolutionResolveLinkIsSuccess,
  getShortenerResolutionSelectedTargetLink,
} from '../../../reducers/tasks/shortenerResolution';
import ShortenerResolutionActionsButton from './ShortenerResolutionActionsButton';
import { translate } from '../../../helpers/languageHelper';

const ShortenerResolutionActions = ({
  context,
  actionsAreLoading,
  closeLinkIsSuccess,
  delayLinkIsSuccess,
  filtersSelected: {
    filterByCustomer: selectedFilterByCustomer,
    domain: selectedDomain,
  },
  linkToResolve,
  resolveLinkIsSuccess,
  shortenerResolutionCloseLink,
  shortenerResolutionDelayLink,
  shortenerResolutionFetchLinkToResolve,
  shortenerResolutionResolveLink,
  targetLink,
}) => {
  const customerId = linkToResolve?.customerId ?? null;
  const newContext = Object.assign(new Context(), context, {
    customerId,
  });

  useEffect(() => {
    const mustFetchTheNextLink =
      resolveLinkIsSuccess || closeLinkIsSuccess || delayLinkIsSuccess;
    if (mustFetchTheNextLink) {
      shortenerResolutionFetchLinkToResolve({
        domainIds: selectedDomain?.domainId
          ? [selectedDomain.domainId]
          : undefined,
        customerId: selectedFilterByCustomer ? customerId : undefined,
      });
    }
  }, [
    closeLinkIsSuccess,
    delayLinkIsSuccess,
    resolveLinkIsSuccess,
    selectedDomain,
    shortenerResolutionFetchLinkToResolve,
    selectedFilterByCustomer,
    customerId,
  ]);

  const handleDone = useCallback(() => {
    if (isValidUrl(targetLink)) {
      const { shortenerLinkId } = linkToResolve;
      shortenerResolutionResolveLink({
        context: newContext,
        linkId: shortenerLinkId,
        urlToResolve: targetLink,
      });
    } else {
      Notification.error({
        message: translate(
          'tasks.shortenerResolution.messages.errors.invalidUrl'
        ),
      });
    }
  }, [linkToResolve, newContext, shortenerResolutionResolveLink, targetLink]);

  const handleClose = useCallback(() => {
    if (linkToResolve) {
      const { shortenerLinkId } = linkToResolve;
      shortenerResolutionCloseLink({
        context: newContext,
        linkId: shortenerLinkId,
        link: linkToResolve,
      });
    }
  }, [linkToResolve, newContext, shortenerResolutionCloseLink]);

  const handleDelay = useCallback(() => {
    if (linkToResolve) {
      const { shortenerLinkId } = linkToResolve;
      shortenerResolutionDelayLink({
        context: newContext,
        linkId: shortenerLinkId,
      });
    }
  }, [linkToResolve, newContext, shortenerResolutionDelayLink]);

  return (
    <div className="rp-shortener-resolution-actions">
      <Row gutter={16} type="flex" justify="start">
        <Col>
          <ShortenerResolutionActionsButton
            disabled={actionsAreLoading || !targetLink}
            onClick={handleDone}
            title={translate('tasks.shortenerResolution.actions.done')}
          >
            <CheckCircleOutlined disabled={actionsAreLoading || !targetLink} />
          </ShortenerResolutionActionsButton>
        </Col>
        <Col>
          <ShortenerResolutionActionsButton
            disabled={actionsAreLoading}
            onClick={handleClose}
            title={translate('tasks.shortenerResolution.actions.close')}
          >
            <CloseCircleOutlined disabled={actionsAreLoading} />
          </ShortenerResolutionActionsButton>
        </Col>
        <Col>
          <ShortenerResolutionActionsButton
            disabled={actionsAreLoading}
            onClick={handleDelay}
            title={translate('tasks.shortenerResolution.actions.delay')}
          >
            <PauseCircleOutlined disabled={actionsAreLoading} />
          </ShortenerResolutionActionsButton>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = state => ({
  actionsAreLoading: getShortenerResolutionActionsAreLoading(state),
  closeLinkIsSuccess: getShortenerResolutionCloseLinkIsSuccess(state),
  delayLinkIsSuccess: getShortenerResolutionDelayLinkIsSuccess(state),
  filtersSelected: getShortenerResolutionFiltersSelected(state),
  linkToResolve: getShortenerResolutionLinkToResolve(state),
  resolveLinkIsSuccess: getShortenerResolutionResolveLinkIsSuccess(state),
  targetLink: getShortenerResolutionSelectedTargetLink(state),
});

export default compose(
  withContext,
  connect(mapStateToProps, {
    shortenerResolutionCloseLink,
    shortenerResolutionDelayLink,
    shortenerResolutionFetchLinkToResolve,
    shortenerResolutionResolveLink,
  })
)(ShortenerResolutionActions);
